<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, ref } from "vue";
import { useValidationHelpers, createLeeftijdRangeValidator } from "@/helpers/validation.helper";
import BestelVraagInput from "./BestelVraagInput.vue";
import type { KindItem } from "@/stores/sportactiviteit-bestel.store";
import type { KindProfiel } from "@/repositories/profile.repository";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";

const props = defineProps<{
    item: KindItem;
    index: number;
    availableProfileKinderen?: KindProfiel[];
}>();

const emits = defineEmits<{ 
    (e: "remove"): void 
}>();

const store = useSportactiviteitBestelStore();

const rules = {
    item: {
        Voornaam: { required: helpers.withMessage("Dit veld is verplicht", required) },
        Achternaam: { required: helpers.withMessage("Dit veld is verplicht", required) },
        Leeftijd: {
            required: helpers.withMessage("Dit veld is verplicht", required),
            leeftijdRange: createLeeftijdRangeValidator(
                () => store.activiteit?.MinLeeftijd ?? 0,
                () => store.activiteit?.MaxLeeftijd ?? 999,
            ),
        },
    },
};

const v$ = useVuelidate(rules, props);
const { getFieldErrors } = useValidationHelpers(v$);

const showSuggestions = ref(false);

const suggestions = computed(() => {
    if (!props.availableProfileKinderen?.length) return [];
    const q = (props.item.Voornaam ?? "").toLowerCase().trim();
    const filtered = q
        ? props.availableProfileKinderen.filter((k) => k.Voornaam.toLowerCase().includes(q))
        : props.availableProfileKinderen;
    return filtered.slice().sort((a, b) => a.Voornaam.localeCompare(b.Voornaam));
});

const onVoornaamFocus = () => { showSuggestions.value = true; };
const onVoornaamBlur = () => { setTimeout(() => { showSuggestions.value = false; }, 150); };

const selectKind = (kind: KindProfiel) => {
    props.item.Id = kind.Id;
    props.item.Voornaam = kind.Voornaam;
    props.item.Achternaam = kind.Achternaam;
    props.item.Leeftijd = kind.Leeftijd;
    showSuggestions.value = false;
    v$.value.item.Voornaam.$touch();
};
</script>

<template>
    <div class="mb-3">
        <h2 class="h4 mb-2">
            Gegevens kind {{ index + 1 }}
            <a href="#" class="font-size-small text-muted ml-1 text-decoration-none" @click.prevent="emits('remove')" aria-label="Verwijder kind">
                <span class="icon-delete" aria-hidden="true"></span>
            </a>
        </h2>
        <div class="row">
            <!-- Voornaam -->
            <div class="col-sm-6" :class="{ 'has-error': v$.item.Voornaam.$error }">
                <div class="form-label-group mb-2" style="position: relative;">
                    <input
                        type="text"
                        class="form-label-group-input"
                        :id="`kind-${index}-Voornaam`"
                        :name="`kind-${index}-Voornaam`"
                        v-model="item.Voornaam"
                        placeholder="Voornaam"
                        autocomplete="off"
                        @focus="onVoornaamFocus"
                        @blur="onVoornaamBlur"
                        @input="item.Id = undefined"
                        @blur.capture="v$.item.Voornaam.$touch()"
                    />
                    <label :for="`kind-${index}-Voornaam`">Voornaam <span class="text-danger">*</span></label>
                    <ul v-if="showSuggestions && suggestions.length" class="kind-typeahead-dropdown">
                        <li
                            v-for="kind in suggestions"
                            :key="kind.Id"
                            @mousedown.prevent="selectKind(kind)"
                        >
                            {{ kind.Voornaam }} {{ kind.Achternaam }}
                            <span class="kind-typeahead-age">{{ kind.Leeftijd }} jaar</span>
                        </li>
                    </ul>
                    <div v-for="err in getFieldErrors('item.Voornaam')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Achternaam -->
            <div class="col-sm-6" :class="{ 'has-error': v$.item.Achternaam.$error }">
                <div class="form-label-group mb-2">
                    <input
                        type="text"
                        class="form-label-group-input"
                        :id="`kind-${index}-Achternaam`"
                        :name="`kind-${index}-Achternaam`"
                        v-model="item.Achternaam"
                        placeholder="Achternaam"
                        @blur="v$.item.Achternaam.$touch()"
                    />
                    <label :for="`kind-${index}-Achternaam`">Achternaam <span class="text-danger">*</span></label>
                    <div v-for="err in getFieldErrors('item.Achternaam')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Leeftijd -->
            <div class="col-sm-12" :class="{ 'has-error': v$.item.Leeftijd.$error }">
                <div class="form-label-group">
                    <input
                        type="number"
                        class="form-label-group-input"
                        :id="`kind-${index}-Leeftijd`"
                        :name="`kind-${index}-Leeftijd`"
                        v-model.number="item.Leeftijd"
                        min="1"
                        max="17"
                        placeholder="Leeftijd"
                        @blur="v$.item.Leeftijd.$touch()"
                    />
                    <label :for="`kind-${index}-Leeftijd`">Leeftijd</label>
                    <div v-for="err in getFieldErrors('item.Leeftijd')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Individuele vragen -->
            <div v-if="item.Vragen.length" class="col-sm-12 mt-2">
                <BestelVraagInput
                    v-for="vraagAntwoord in item.Vragen"
                    :key="vraagAntwoord.Id"
                    :vraag-antwoord="vraagAntwoord"
                    :prefix="`kind-${index}`"
                />
            </div>
        </div>
    </div>
</template>